/**
 * Sales-document upload + download helpers.
 *
 * Unlike `lib/mediaR2.js` (which is for gallery items / job-card photos),
 * these talk to /api/sales-documents and /api/media. The upload path still
 * uses the existing `/media/presign-upload` endpoint — it returns a signed
 * PUT URL independent of the destination collection. We just persist the
 * resulting `r2_key` in the sales_documents record, not in gallery_items.
 */
import api from "./api";

export async function salesDocPresign({ customerId, file }) {
  const { data } = await api.post("/media/presign-upload", {
    customer_id: customerId,
    filename: file.name || "upload",
    content_type: file.type || "application/octet-stream",
    size: file.size,
    parent_type: "sales_documents",
  });
  return data; // { key, url, content_type, ... }
}

/** Browser-side direct PUT to R2 with progress reporting. */
export function salesDocPutToR2(presign, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presign.url, true);
    xhr.setRequestHeader("Content-Type", presign.content_type);
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`R2 upload failed (HTTP ${xhr.status})`));
    xhr.onerror = () => reject(new Error("Network error during R2 upload"));
    xhr.send(file);
  });
}

/**
 * Request a short-lived signed GET URL for an R2-backed sales document.
 * `download=true` returns a URL that forces the browser to save to disk
 * with the original filename.
 */
export async function salesDocDownloadUrl(docId, { download = false } = {}) {
  const qs = download ? "?download=true" : "";
  const { data } = await api.get(`/sales-documents/${docId}/download-url${qs}`);
  return data; // { url, filename, content_type, size }
}

/** Trigger a device-side save (Save to Downloads / Save to Files).
 *
 * We deliberately AVOID `fetch().blob()` against R2 — that triggers a CORS
 * preflight which the bucket doesn't permit, and was the root cause of "PDF
 * docs cannot download" in production. The presigned URL itself already
 * carries `Content-Disposition: attachment; filename=...` (see
 * `r2_storage.presigned_get`), so a top-level anchor navigation is enough
 * to make every modern browser save the file straight to disk. Top-level
 * navigation isn't subject to the bucket's CORS policy.
 */
export async function downloadSalesDocToDevice(doc) {
  const info = await salesDocDownloadUrl(doc.id, { download: true });
  const a = document.createElement("a");
  a.href = info.url;
  // `download` is ignored cross-origin, but harmless — keep as a hint for
  // same-origin proxies. The Content-Disposition header from R2 is what
  // actually forces the save behaviour.
  a.download = info.filename || doc.file_name || "document";
  a.rel = "noopener";
  // Open in same context — the browser sees the attachment header and
  // never actually navigates away from the current page.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
